import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useUpdateAssistant, AssistantDetail } from '@/hooks/useAssistants'

interface PromptTabProps {
  assistantId: string
  assistant: AssistantDetail
}

interface PromptSettings {
  system_prompt: string
  opener: string
  empty_response: string
  similarity_threshold: number
  keywords_similarity_weight: number
  top_n: number
  show_quote: boolean
}

const DEFAULT_SYSTEM_PROMPT = `You are an intelligent assistant. Please summarize the content of the knowledge base to answer the question. Please list the data in the knowledge base and answer in detail. When all knowledge base content is irrelevant to the question, your answer must include the sentence "The answer you are looking for is not found in the knowledge base!" Answers need to consider chat history. Here is the knowledge base: {knowledge} The above is the knowledge base.`

const DEFAULT_SETTINGS: PromptSettings = {
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  opener: 'Hi! I am your assistant, can I help you?',
  empty_response: "I couldn't find any relevant information in my knowledge base.",
  similarity_threshold: 0.2,
  keywords_similarity_weight: 0.7,
  top_n: 6,
  show_quote: true,
}

export function PromptTab({ assistantId, assistant }: PromptTabProps) {
  const { toast } = useToast()
  const updateAssistant = useUpdateAssistant()

  const [settings, setSettings] = useState<PromptSettings>({
    system_prompt: assistant.system_prompt,
    opener: assistant.opener,
    empty_response: assistant.empty_response,
    similarity_threshold: assistant.similarity_threshold,
    keywords_similarity_weight: assistant.keywords_similarity_weight,
    top_n: assistant.top_n,
    show_quote: assistant.show_quote,
  })

  const [hasChanges, setHasChanges] = useState(false)

  // Track changes
  useEffect(() => {
    const changed =
      settings.system_prompt !== assistant.system_prompt ||
      settings.opener !== assistant.opener ||
      settings.empty_response !== assistant.empty_response ||
      settings.similarity_threshold !== assistant.similarity_threshold ||
      settings.keywords_similarity_weight !== assistant.keywords_similarity_weight ||
      settings.top_n !== assistant.top_n ||
      settings.show_quote !== assistant.show_quote
    setHasChanges(changed)
  }, [settings, assistant])

  const handleSave = async () => {
    try {
      await updateAssistant.mutateAsync({
        assistantId,
        data: settings,
      })
      toast({
        title: 'Settings saved',
        description: 'Prompt settings have been saved successfully.',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save settings',
        variant: 'destructive',
      })
    }
  }

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Prompt & Retrieval Settings</CardTitle>
          <CardDescription>Configure how the assistant responds and retrieves information</CardDescription>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || updateAssistant.isPending}>
          {updateAssistant.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* System Prompt */}
        <div className="space-y-2">
          <Label htmlFor="system_prompt">System Prompt</Label>
          <Textarea
            id="system_prompt"
            value={settings.system_prompt}
            onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
            rows={12}
            className="resize-y font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Use {'{knowledge}'} variable to reference retrieved content
          </p>
        </div>

        {/* Opening Message */}
        <div className="space-y-2">
          <Label htmlFor="opener">Opening Message</Label>
          <Input
            id="opener"
            value={settings.opener}
            onChange={(e) => setSettings({ ...settings, opener: e.target.value })}
            placeholder="Hi! I am your assistant, can I help you?"
          />
          <p className="text-xs text-muted-foreground">First message shown to users</p>
        </div>

        {/* Empty Response */}
        <div className="space-y-2">
          <Label htmlFor="empty_response">Empty Response</Label>
          <Input
            id="empty_response"
            value={settings.empty_response}
            onChange={(e) => setSettings({ ...settings, empty_response: e.target.value })}
            placeholder="I couldn't find any relevant information..."
          />
          <p className="text-xs text-muted-foreground">
            Response when no relevant information is found
          </p>
        </div>

        {/* Similarity Threshold */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label>Similarity Threshold</Label>
            <span className="text-sm font-medium">{settings.similarity_threshold.toFixed(2)}</span>
          </div>
          <Slider
            value={[settings.similarity_threshold]}
            onValueChange={([value]) => setSettings({ ...settings, similarity_threshold: value })}
            min={0}
            max={1}
            step={0.05}
          />
          <p className="text-xs text-muted-foreground">
            Minimum similarity score for retrieved chunks
          </p>
        </div>

        {/* Keywords Similarity Weight */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label>Keywords Similarity Weight</Label>
            <span className="text-sm font-medium">{settings.keywords_similarity_weight.toFixed(2)}</span>
          </div>
          <Slider
            value={[settings.keywords_similarity_weight]}
            onValueChange={([value]) => setSettings({ ...settings, keywords_similarity_weight: value })}
            min={0}
            max={1}
            step={0.05}
          />
          <p className="text-xs text-muted-foreground">
            Weight of keyword matching vs vector similarity
          </p>
        </div>

        {/* Top N Chunks */}
        <div className="space-y-2">
          <Label htmlFor="top_n">Top N Chunks</Label>
          <Input
            id="top_n"
            type="number"
            min={1}
            max={20}
            value={settings.top_n}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10)
              if (!isNaN(value) && value >= 1 && value <= 20) {
                setSettings({ ...settings, top_n: value })
              }
            }}
            className="w-24"
          />
          <p className="text-xs text-muted-foreground">
            Number of most relevant chunks to retrieve (1-20)
          </p>
        </div>

        {/* Show Quote */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="show_quote">Show Quote</Label>
            <p className="text-xs text-muted-foreground">
              Display source references in responses
            </p>
          </div>
          <Switch
            id="show_quote"
            checked={settings.show_quote}
            onCheckedChange={(checked) => setSettings({ ...settings, show_quote: checked })}
          />
        </div>

        {/* Bottom buttons */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={handleReset}>
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || updateAssistant.isPending}>
            {updateAssistant.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
